import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Autocomplete,
  InputAdornment,
  MenuItem,
  TextField,
  type AutocompleteProps,
  type AutocompleteRenderGetTagProps,
  type AutocompleteRenderInputParams,
  type MenuItemProps,
} from '@mui/material';
import React, { useMemo } from 'react';
import CategoryChip from '~/components/CategoryChip';
import type { Suggestions } from '~/types';

export type CategoryFilterItem = Omit<Suggestions.Category, 'total'>;

interface CategoryFilterDropdownProps
  extends Omit<
    AutocompleteProps<CategoryFilterItem, boolean, false, false>,
    'options' | 'renderInput' | 'value' | 'onChange' | 'defaultValue'
  > {
  multiple?: boolean;
  value: CategoryFilterItem[];
  categories: CategoryFilterItem[];
  fullWidth?: boolean;
  onChange: (value: CategoryFilterItem[]) => void;
}

export const CategoryFilterDropdown = ({
  categories,
  value,
  onChange,
  fullWidth,
  multiple = true,
  ...rest
}: CategoryFilterDropdownProps) => {
  const options = useSortOptions(categories);
  const placeholder = usePlaceholderText(value, categories);

  return (
    <Autocomplete<CategoryFilterItem, boolean>
      {...rest}
      options={options}
      multiple={multiple}
      openOnFocus
      fullWidth={fullWidth}
      value={multiple ? value : (value[0] ?? null)}
      onChange={(_event, newValue) => {
        if (Array.isArray(newValue)) {
          onChange(newValue);
        } else {
          onChange(newValue ? [newValue] : []);
        }
      }}
      getOptionLabel={(option) => `${option.name}`}
      disableCloseOnSelect
      filterSelectedOptions={false}
      filterOptions={(opts, state) => filterOptions(opts, state.inputValue)}
      renderOption={renderOption}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderTags={(val, getTagProps) => {
        if (Array.isArray(val)) {
          return renderTags(val, getTagProps);
        }
        return renderTags([val], getTagProps);
      }}
      groupBy={(option) => option.industries_name}
      renderInput={(params) => renderInput(placeholder, params)}
    />
  );
};

function useSortOptions(categories: CategoryFilterItem[]) {
  const options = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (a.industries_id !== b.industries_id) {
        return a.industries_id - b.industries_id;
      }
      return a.name.localeCompare(b.name);
    });
  }, [categories]);
  return options;
}

function filterOptions(options: CategoryFilterItem[], text: string) {
  return options.filter(
    (option) =>
      option.name.toLowerCase().includes(text.toLowerCase()) ||
      option.industries_name.toLowerCase().includes(text.toLowerCase())
  );
}

function renderOption(
  { key, ...props }: MenuItemProps & { key: React.Key },
  option: CategoryFilterItem,
  { selected }: { selected: boolean }
) {
  const SelectionIcon = selected ? CheckBox : CheckBoxOutlineBlank;
  return (
    <MenuItem key={key} {...props}>
      <SelectionIcon
        fontSize="small"
        style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
      />
      <CategoryChip
        category={option.name}
        industries_id={option.industries_id}
      />
    </MenuItem>
  );
}

function renderTags(
  value: CategoryFilterItem[],
  getTagProps: AutocompleteRenderGetTagProps
) {
  return value.map((option, index) => {
    const { key, ...tagProps } = getTagProps({ index });
    return (
      <CategoryChip
        category={option.name}
        industries_id={option.industries_id}
        key={key}
        {...tagProps}
      />
    );
  });
}

function renderInput(
  placeholder: string,
  params: AutocompleteRenderInputParams
) {
  return (
    <TextField
      {...params}
      label="Filter Categories"
      size={params.size}
      placeholder={placeholder}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <FilterAltIcon />
          </InputAdornment>
        ),
      }}
    />
  );
}

function usePlaceholderText(
  value: CategoryFilterItem[] | CategoryFilterItem | null,
  categories: CategoryFilterItem[]
) {
  const placeholder = useMemo(() => {
    if (Array.isArray(value)) {
      return `Filter${value.length ? 'ing' : ''} ${
        value.length || categories.length
      } Categories`;
    }
    return `Filter ${categories.length} Categories`;
  }, [value, categories]);
  return placeholder;
}
